import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, del, get, post, type Programme, type RundownDto, type RundownItemDto } from '../api.js';
import { t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { navigate } from '../router.js';
import { fmtDateInput, hhmmss, mmss, todayIso } from '../util.js';
import type { SyncInfo } from './rundown-grid.js';

/** The dialogs open now, oldest first: Escape closes only the top one (the player over the clip chooser, not both). */
const openDialogs: object[] = [];

export function Dialog({ title, onClose, children, width }: { title: string; onClose: () => void; children: ReactNode; width?: number }) {
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const me = {};
    openDialogs.push(me);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && openDialogs[openDialogs.length - 1] === me && close.current();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      openDialogs.splice(openDialogs.indexOf(me), 1);
    };
  }, []);
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={width ? { width } : undefined} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="dialog-title">
          <span>{title}</span>
          <button onClick={onClose} aria-label={t('cancel')}>
            ✕
          </button>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}

/** The edit (Μοντάζ) a story plays a clip through, as a preview needs it. */
export interface PreviewEdit {
  id: string;
  title: string;
  rendered: boolean;
  renderedAt: string | null;
  durationMs: number | null;
  /** It airs as the clip itself, cut, under the story's titles. */
  plain: boolean;
}

/** What a preview plays and says about it: the clip; for a clip with an edit, its export, else the cut (a plain one) or the clip. */
export function previewOf(mediaAssetId: string, edit?: PreviewEdit | null, cut?: { inMs: number | null; outMs: number | null }): { src: string; note: string } {
  const clip = `/api/media/${encodeURIComponent(mediaAssetId)}/proxy`;
  if (!edit) return { src: clip, note: '' };
  if (edit.rendered) return { src: `/api/edits/${encodeURIComponent(edit.id)}/output${edit.renderedAt ? `?v=${encodeURIComponent(edit.renderedAt)}` : ''}`, note: `✂ ${t('cuePreviewEdit')}${edit.durationMs ? ` · ${hhmmss(Math.round(edit.durationMs / 1000))}` : ''}` };
  if (!edit.plain) return { src: clip, note: `✂ ${t('cuePreviewNoExport')}` };
  // the cut, as a media fragment: the player starts at IN and stops at OUT
  const at = cut && (cut.inMs !== null || cut.outMs !== null) ? `#t=${(cut.inMs ?? 0) / 1000}${cut.outMs !== null ? `,${cut.outMs / 1000}` : ''}` : '';
  return { src: clip + at, note: `✂ ${t('cuePreviewCut')}` };
}

/**
 * The clip, played from the playout's proxy copy through the newsroom API. A clip the story plays through an edit
 * plays the edit: its export, as the editor showed it; without one, the cut of a plain edit from IN to OUT.
 */
export function PreviewDialog({ mediaAssetId, title, meta, onClose, edit, cut }: { mediaAssetId: string; title: string; meta: string; onClose: () => void; edit?: PreviewEdit | null; cut?: { inMs: number | null; outMs: number | null } }) {
  const [failed, setFailed] = useState(false);
  const { src, note } = previewOf(mediaAssetId, edit, cut);
  const about = [note, meta].filter(Boolean).join(' · ');
  return (
    <Dialog title={`▶ ${edit ? `✂ ${edit.title}` : title}`} onClose={onClose} width={720}>
      {failed ? <div className="banner warn">{t('cueNoPreview')}</div> : <video className="preview-video" controls autoPlay playsInline src={src} onError={() => setFailed(true)} />}
      {about && <p className="muted small">{about}</p>}
      <div className="toolbar">
        <button className="btn" type="button" onClick={onClose}>
          {t('back')}
        </button>
      </div>
    </Dialog>
  );
}

function useProgrammes(): Programme[] {
  const [p, setP] = useState<Programme[]>([]);
  useEffect(() => {
    get<{ programmes: Programme[] }>('/api/programmes').then((r) => setP(r.programmes));
  }, []);
  return p;
}

// ── Autocue ────────────────────────────────────────────────────────────────────
interface Device {
  id: string;
  name: string;
  systemName: string;
  profile: string;
  formatName: string | null;
}
interface Preview {
  revision: number;
  filename: string;
  profile: string;
  items: number;
  lines: number;
  bytes: number;
  unsupported: Array<{ line: number; column: number; char: string; codePoint: number; item: { title: string; displayNumber: string | null } | null }>;
  preview: string[];
}

export function AutocueDialog({ rundownId, onClose }: { rundownId: string; onClose: () => void }) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [profile, setProfile] = useState<'hpress-cp1253' | 'utf8'>('hpress-cp1253');
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const [result, setResult] = useState<{ filename: string; bytes: number; sha256: string; folder: string; exportId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preview: Preview | null = previews[profile] ?? null;

  useEffect(() => {
    get<{ devices: Device[] }>('/api/autocue-devices').then((r) => {
      setDevices(r.devices);
      if (r.devices[0]) setDeviceId(r.devices[0].id);
    });
  }, []);
  useEffect(() => {
    let cancelled = false;
    post<Preview>(`/api/rundowns/${rundownId}/exports/autocue/preview`, { profile })
      .then((p) => !cancelled && setPreviews((prev) => ({ ...prev, [profile]: p })))
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [rundownId, profile]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await post(`/api/rundowns/${rundownId}/exports/autocue`, { deviceId, profile }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const blocked = profile === 'hpress-cp1253' && (preview?.unsupported.length ?? 0) > 0;
  return (
    <Dialog title={`${t('autocue')} — ${t('preview')}`} onClose={onClose} width={860}>
      <div className="form-grid" style={{ padding: 0, gridTemplateColumns: 'max-content 1fr max-content 1fr' }}>
        <label>{t('device')} :</label>
        <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} ({d.systemName}, {d.formatName ?? '—'})
            </option>
          ))}
        </select>
        <label>{t('profile')} :</label>
        <select value={profile} onChange={(e) => setProfile(e.target.value as 'hpress-cp1253' | 'utf8')}>
          <option value="hpress-cp1253">{t('profileLegacy')}</option>
          <option value="utf8">{t('profileUtf8')}</option>
        </select>
      </div>
      {preview && (
        <p className="muted" style={{ margin: '6px 0' }}>
          {preview.filename} · {preview.items} items · {preview.lines} lines · {preview.bytes} bytes · {t('revision')} {preview.revision}
        </p>
      )}
      {preview && (
        <div className={`banner ${blocked ? 'err' : 'info'}`}>
          {blocked ? (
            <div>
              <b>{t('unsupportedChars')}:</b>
              <ul style={{ margin: '4px 0 0 16px' }}>
                {preview.unsupported.slice(0, 20).map((u, i) => (
                  <li key={i}>
                    “{u.char}” U+{u.codePoint.toString(16).toUpperCase().padStart(4, '0')} — {t('line')} {u.line}, {t('column')} {u.column}
                    {u.item ? ` — [${u.item.displayNumber ?? ''}] ${u.item.title}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            t('noUnsupported')
          )}
        </div>
      )}
      {preview && <pre className="preview">{preview.preview.join('\n')}</pre>}
      {error && <div className="banner err">{error}</div>}
      {result && (
        <div className="banner info">
          {t('fileWritten')}: <b>{result.filename}</b> ({result.bytes} bytes, sha256 {result.sha256.slice(0, 12)}…) → {result.folder}
          <a className="btn" href={`/api/exports/${result.exportId}/download`}>
            {t('download')}
          </a>
        </div>
      )}
      <div className="toolbar">
        <button className="btn" onClick={onClose}>
          {t('backToGuide')}
        </button>
        <button className="btn primary" disabled={busy || blocked || !deviceId || !preview} onClick={() => void send()}>
          {t('autocue')} — {t('send')}
        </button>
      </div>
    </Dialog>
  );
}

// ── Copy bulletin ──────────────────────────────────────────────────────────────
export function CopyBulletinDialog({ rundownId, current, onClose }: { rundownId: string; current: { programmeId: string; date: string }; onClose: () => void }) {
  const programmes = useProgrammes();
  const [programmeId, setProgrammeId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);

  const copy = async () => {
    if (!programmeId) return;
    if (!window.confirm(t('copyConfirm'))) return;
    setState('busy');
    try {
      const r = await post<{ rundownId: string; copied: number }>(`/api/rundowns/${rundownId}/copy`, { programmeId, date });
      setMessage(`${t('copyDone')}: ${r.copied}`);
      setTarget(r.rundownId);
      setState('done');
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
      setState('idle');
    }
  };
  return (
    <Dialog title={`${t('copyTo')} (${fmtDateInput(current.date)})`} onClose={onClose}>
      <div className="form-grid" style={{ padding: 0, gridTemplateColumns: 'max-content 1fr' }}>
        <label className="req">{t('bulletinDate')} :</label>
        <DateInput value={date} onChange={setDate} />
        <label className="req">{t('bulletin')} :</label>
        <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
          <option value="">{t('choose')}</option>
          {programmes.map((p) => (
            <option key={p.id} value={p.id} disabled={p.id === current.programmeId && date === current.date}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      {message && <div className={`banner ${state === 'done' ? 'info' : 'err'}`}>{message}</div>}
      <div className="toolbar">
        {state === 'done' && target ? (
          <>
            <button className="btn" onClick={() => navigate(`/rundown/${target}`)}>
              → {t('rundownGuide')}
            </button>
            <button className="btn primary" onClick={onClose}>
              {t('back')}
            </button>
          </>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              {t('cancel')}
            </button>
            <button className="btn primary" disabled={!programmeId || state === 'busy'} onClick={() => void copy()}>
              {t('copy')}
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Selected stories: copy elsewhere / print / autocue ─────────────────────────
export function SelectedDialog({ rundownId, items, onClose }: { rundownId: string; items: RundownItemDto[]; onClose: () => void }) {
  const programmes = useProgrammes();
  const [programmeId, setProgrammeId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [mode, setMode] = useState<'copy' | 'linked'>('copy');
  const [message, setMessage] = useState<string | null>(null);
  const total = items.reduce((s, i) => s + i.totalS, 0);
  const copy = async () => {
    if (!programmeId) return;
    try {
      const r = await post<{ copied: number; rundownId: string }>(`/api/rundowns/${rundownId}/copy-items`, { itemIds: items.map((i) => i.id), programmeId, date, mode });
      setMessage(`${t('copyDone')}: ${r.copied}`);
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    }
  };
  return (
    <Dialog title={t('selectedActions')} onClose={onClose} width={760}>
      <div className="form-grid" style={{ padding: 0 }}>
        <label className="req">{t('bulletinDate')} :</label>
        <DateInput value={date} onChange={setDate} />
        <label className="req">{t('bulletin')} :</label>
        <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
          <option value="">{t('choose')}</option>
          {programmes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <label>Τρόπος :</label>
        <select value={mode} onChange={(e) => setMode(e.target.value as 'copy' | 'linked')} className="wide">
          <option value="copy">{t('duplicateCopy')} — όπως το HPress</option>
          <option value="linked">{t('duplicateLinked')} — το ίδιο κείμενο και στα δύο δελτία</option>
        </select>
      </div>
      <div className="toolbar left">
        <button className="btn primary" disabled={!programmeId} onClick={() => void copy()}>
          {t('copy')}
        </button>
        <a className="btn" href={`/api/rundowns/${rundownId}/exports/pdf-full?items=${items.map((i) => i.id).join(',')}`} target="_blank" rel="noreferrer">
          🖨 {t('printSelected')}
        </a>
      </div>
      {message && <div className="banner info">{message}</div>}
      <table className="grid">
        <thead>
          <tr>
            <th>{t('colNum')}</th>
            <th>{t('colTitle')}</th>
            <th>{t('colTotal')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id}>
              <td className="num">{i.displayNumber ?? '-'}</td>
              <td>{i.title}</td>
              <td className="num">{mmss(i.totalS)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="tip">
        {items.length} ειδήσεις · {mmss(total)}
      </p>
      <div className="toolbar">
        <button className="btn" onClick={onClose}>
          {t('backToGuide')}
        </button>
      </div>
    </Dialog>
  );
}

// ── Publish / link ─────────────────────────────────────────────────────────────
export function PublishDialog({ rundown, sync, onClose, onChanged }: { rundown: RundownDto; sync: SyncInfo; onClose: () => void; onChanged: () => void }) {
  const [playerId, setPlayerId] = useState(sync.links.find((l) => l.target === 'playout')?.targetRef ?? '1');
  const [players, setPlayers] = useState<Array<{ id: string; name: string; enabled: boolean; mode: string | null; state: string | null; onAirTitle: string | null }>>([]);
  useEffect(() => {
    get<{ players: typeof players }>('/api/integrations/players')
      .then((r) => setPlayers(r.players))
      .catch(() => undefined);
  }, []);
  const [roId, setRoId] = useState(sync.links.find((l) => l.target === 'mos')?.targetRef ?? `airpost.${rundown.date}.${rundown.programmeName.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}`);
  const [message, setMessage] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setMessage(null);
      onChanged();
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    }
  };
  const link = (l: 'playout' | 'mos' | undefined) => sync.links.find((x) => x.target === l);
  const row = (target: 'playout' | 'mos', ref: string, setRef: (v: string) => void, label: string) => {
    const l = link(target);
    return (
      <tr>
        <td>
          <b>{label}</b>
        </td>
        <td>
          {target === 'playout' && players.length > 0 ? (
            <select value={ref} onChange={(e) => setRef(e.target.value)} style={{ width: 260 }} disabled={!!l}>
              {!players.some((p) => p.id === ref) && <option value={ref}>{ref}</option>}
              {players.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.enabled}>
                  {p.id} · {p.name}
                  {p.mode ? ` · ${p.mode}` : ''}
                  {p.onAirTitle ? ` · ${t('playoutOnAir')}: ${p.onAirTitle}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input value={ref} onChange={(e) => setRef(e.target.value)} style={{ width: 220 }} disabled={!!l} title={target === 'playout' ? t('noPlayersYet') : undefined} />
          )}
        </td>
        <td>{l ? <span className={`status ${l.state === 'synced' ? 'ok' : l.state === 'pending' ? 'warn' : 'err'}`}>{t(l.state as 'pending')}</span> : <span className="status">{t('unlinked')}</span>}</td>
        <td className="muted">{l ? `${t('revision')} ${l.lastAppliedRevision ?? '—'}${l.lastLatencyMs !== null && l.lastLatencyMs !== undefined ? ` · ${l.lastLatencyMs} ms` : ''}${l.lastError ? ` · ${l.lastError}` : ''}` : ''}</td>
        <td>
          {l ? (
            <button className="btn" onClick={() => void act(() => del(`/api/rundowns/${rundown.id}/link/${target}`))}>
              Αποσύνδεση
            </button>
          ) : (
            <button className="btn" onClick={() => void act(() => post(`/api/rundowns/${rundown.id}/link`, { target, targetRef: ref }))}>
              Σύνδεση
            </button>
          )}
        </td>
      </tr>
    );
  };
  const blocked = sync.links.flatMap((l) => l.blockedItems.map((b) => ({ ...b, target: l.target })));
  return (
    <Dialog title={`${t('publish')} — ${rundown.programmeName} ${fmtDateInput(rundown.date)}`} onClose={onClose} width={820}>
      <p className="muted">
        Κατάσταση: <b>{t(rundown.status as 'draft')}</b> · {t('revision')} {rundown.revision} · δημοσιευμένη {rundown.publishedRevision ?? '—'}. Οι συντακτικές αλλαγές δεν εκτελούν ποτέ TAKE/PLAY/STOP ούτε CG ON AIR.
      </p>
      <table className="grid">
        <tbody>
          {row('playout', playerId, setPlayerId, `Playout (${t('playoutPlayer')})`)}
          {row('mos', roId, setRoId, 'Viz / MOS (roID)')}
        </tbody>
      </table>
      {blocked.length > 0 && (
        <div className="banner warn">
          <div>
            <b>Αλλαγές σε αναμονή απόφασης χειριστή:</b>
            <ul style={{ margin: '4px 0 0 16px' }}>
              {blocked.map((b) => (
                <li key={b.target + b.itemId}>
                  {b.target}: {b.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {message && <div className="banner err">{message}</div>}
      <div className="toolbar">
        <button className="btn" onClick={onClose}>
          {t('back')}
        </button>
        <button className="btn primary" disabled={!sync.links.length} onClick={() => void act(() => post(`/api/rundowns/${rundown.id}/publish`))}>
          ⇪ {t('publish')} ({t('revision')} {rundown.revision + 1})
        </button>
      </div>
    </Dialog>
  );
}

// ── Playout media (clip) for a rundown row ─────────────────────────────────────
interface MediaRow {
  id: string;
  title: string;
  filename: string;
  durationMs: number;
  status: string;
}

export function MediaDialog({ item, currentTitle, onClose, onAssign }: { item: { title: string; mediaAssetId: string | null }; currentTitle: string | null; onClose: () => void; onAssign: (mediaAssetId: string | null) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<MediaRow | null>(null);
  const search = (e?: FormEvent) => {
    e?.preventDefault();
    get<{ media: MediaRow[] }>(`/api/media?q=${encodeURIComponent(q.trim())}&limit=60`)
      .then((r) => setRows(r.media))
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };
  useEffect(() => {
    const id = setTimeout(() => search(), 0);
    return () => clearTimeout(id);
    // initial list only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const assign = async (id: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await onAssign(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Dialog title={`${t('mediaDialogTitle')} — ${item.title}`} onClose={onClose} width={760}>
        <p className="tip">{t('mediaHint')}</p>
        <p>
          <b>{t('mediaCurrent')}:</b> {item.mediaAssetId ? (currentTitle ?? item.mediaAssetId) : '—'}
          {item.mediaAssetId && (
            <button className="btn danger" style={{ marginLeft: 10 }} disabled={busy} onClick={() => void assign(null)}>
              {t('mediaClear')}
            </button>
          )}
        </p>
        <form className="toolbar left" onSubmit={search}>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mediaSearch')} style={{ width: 320 }} autoFocus />
          <button className="btn" type="submit">
            🔍 {t('search')}
          </button>
        </form>
        {error && <div className="banner err">{error}</div>}
        <table className="grid compact">
          <thead>
            <tr>
              <th />
              <th>{t('title')}</th>
              <th>{t('videoDuration')}</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="c muted">
                  {t('mediaNone')}
                </td>
              </tr>
            )}
            {rows.map((m) => (
              <tr key={m.id} className={m.id === item.mediaAssetId ? 'cursor' : ''}>
                <td className="media-thumb-cell">
                  <button type="button" className="media-thumb" title={`▶ ${t('cuePreview')}: ${m.title}`} onClick={() => setPlaying(m)}>
                    <img alt="" loading="lazy" src={`/api/media/${encodeURIComponent(m.id)}/thumb`} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                    <span className="play" aria-hidden="true">
                      ▶
                    </span>
                  </button>
                </td>
                <td className="title">
                  {m.title}
                  <div className="muted">{m.filename}</div>
                </td>
                <td className="num">{hhmmss(Math.round(m.durationMs / 1000))}</td>
                <td className="c">
                  <span className={`status ${m.status === 'READY' ? 'ok' : m.status === 'MISSING' ? 'err' : 'warn'}`}>{m.status}</span>
                </td>
                <td className="actions">
                  <button className="btn primary" disabled={busy || m.id === item.mediaAssetId} onClick={() => void assign(m.id)}>
                    {t('mediaAssign')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Dialog>
      {playing && <PreviewDialog mediaAssetId={playing.id} title={playing.title} meta={`${playing.filename} · ${hhmmss(Math.round(playing.durationMs / 1000))}`} onClose={() => setPlaying(null)} />}
    </>
  );
}
